import { useViewAs } from '../contexts/ViewAsContext';
import { useAccount } from '../contexts/AccountContext';
import { useI18n } from '../i18n/I18nProvider';

/**
 * Persistent, unmistakable bar shown to staff while they view the portal as
 * a customer. Rendered through AppShell's `viewAsBanner` slot so it sits
 * above the sidebar and header on every page.
 */
export function ViewAsBanner() {
  const { viewedUser, viewedUserLoading, exitViewAs, auditRecord } = useViewAs();
  const { accounts, ready } = useAccount();
  const { t } = useI18n();

  const name = viewedUser?.full_name?.trim() || viewedUser?.email || '';
  const showEmail = !!viewedUser?.full_name?.trim() && !!viewedUser?.email;
  const accountLabel = !ready
    ? t('common.loading')
    : accounts.length === 1
      ? t('viewas.account_one')
      : t('viewas.accounts', { n: accounts.length });

  return (
    <div
      role="region"
      aria-label={t('viewas.region')}
      data-testid="view-as-banner"
      className="w-full border-b border-warning bg-warning-soft px-3 py-2 text-sm text-text sm:px-4"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-2 font-medium">
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-warning" />
          {viewedUserLoading || !viewedUser ? (
            t('viewas.loading')
          ) : (
            <>
              {t('viewas.viewing_as')} {name}
              {showEmail && <span className="font-normal text-text-muted">({viewedUser.email})</span>}
            </>
          )}
        </span>
        <span aria-hidden="true" className="text-text-faint">·</span>
        <span className="text-text-muted">{accountLabel}</span>
        {auditRecord.status === 'failed' && (
          <>
            <span aria-hidden="true" className="text-text-faint">·</span>
            <span role="status" className="text-notification" title={auditRecord.message}>
              {t('viewas.audit_failed')}
            </span>
          </>
        )}
        <button
          type="button"
          onClick={exitViewAs}
          className="ml-auto inline-flex min-h-11 items-center rounded-md border border-warning bg-surface px-3 text-sm font-medium text-text hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:min-h-8"
        >
          {t('viewas.exit')}
        </button>
      </div>
      <p className="mt-1 text-xs text-text-muted">{t('viewas.read_only')}</p>
    </div>
  );
}
